import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Zap, Flame, Heart, Brain, Pencil, User as UserIcon, Globe } from "lucide-react";

const CAT_ICON = { energy: Zap, hormones: Flame, recovery: Heart, mindset: Brain };
const CAT_COLOR = { energy: "#FF8A00", hormones: "#E52E71", recovery: "#FFB347", mindset: "#FFD58A" };

export default function ProtocolAdminTab({ users = [] }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [filterScope, setFilterScope] = useState("all");

  const load = async () => {
    const { data } = await api.get("/admin/protocol/items");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setEditing(item);
    setForm(item ? {
      label: item.label, description: item.description || "",
      category: item.category, tier_required: item.tier_required,
      order: item.order, active: !!item.active,
      scope: item.scope, user_id: item.user_id || "",
    } : blank());
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 100,
                        user_id: form.scope === "user" ? form.user_id : null };
      if (form.scope === "user" && !payload.user_id) {
        toast.error("Select a user for this personal item");
        return;
      }
      if (editing) {
        await api.patch(`/admin/protocol/items/${editing.item_id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/admin/protocol/items", payload);
        toast.success("Item created");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try {
      await api.delete(`/admin/protocol/items/${item.item_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/admin/protocol/items/${item.item_id}`, {
        label: item.label, description: item.description || "",
        category: item.category, tier_required: item.tier_required,
        order: item.order, active: !item.active,
        scope: item.scope, user_id: item.user_id || null,
      });
      load();
    } catch { toast.error("Update failed"); }
  };

  const userName = (uid) => users.find((u) => u.user_id === uid)?.name || uid?.slice(0,8);

  const visible = items.filter((i) => {
    if (filterScope === "global") return i.scope === "global";
    if (filterScope === "user") return i.scope === "user";
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Daily Protocol Library ({items.length})</h2>
          <p className="text-sm text-white/55 mt-1 max-w-2xl">
            Add, edit, or delete the daily protocol items your members see. Each item can be gated to a paid tier or assigned to a specific user.
          </p>
        </div>
        <Button onClick={() => open(null)} data-testid="new-protocol-item"
          className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
          <Plus size={14} className="mr-2" /> New item
        </Button>
      </div>

      <div className="flex gap-2">
        {["all", "global", "user"].map((s) => (
          <button
            key={s}
            onClick={() => setFilterScope(s)}
            data-testid={`protocol-filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-xs tracking-wide capitalize transition-all ${
              filterScope === s
                ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white"
                : "bg-[#0F0F0F] text-white/60 hover:text-white border border-white/5"
            }`}
          >
            {s === "all" ? "All" : s === "global" ? "Global" : "Per-user"}
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[11px] tracking-[0.18em] uppercase text-white/40">
            <tr>
              <th className="text-left px-4 py-3">Item</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Access</th>
              <th className="text-left px-4 py-3">Scope</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((it) => {
              const Icon = CAT_ICON[it.category] || Zap;
              return (
                <tr key={it.item_id} className="border-t border-white/5" data-testid={`protocol-row-${it.item_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.label}</div>
                    <div className="text-[11px] text-white/40 mt-0.5 line-clamp-1">{it.description}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: CAT_COLOR[it.category] }}>
                      <Icon size={12} /> {it.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#FF8A00]">{it.tier_required}</span>
                  </td>
                  <td className="px-4 py-3">
                    {it.scope === "global" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-white/60">
                        <Globe size={12} /> Global
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-[#E52E71]">
                        <UserIcon size={12} /> {userName(it.user_id)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Switch checked={!!it.active} onCheckedChange={() => toggleActive(it)}
                      data-testid={`protocol-active-${it.item_id}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => open(it)}
                      data-testid={`edit-protocol-${it.item_id}`}
                      className="text-white/40 hover:text-white p-2 mr-1">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(it)}
                      data-testid={`delete-protocol-${it.item_id}`}
                      className="text-white/40 hover:text-[#E52E71] p-2">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No items.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg" data-testid="protocol-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit" : "New"} protocol item
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-white/60">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})}
                required data-testid="pi-label"
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                data-testid="pi-description"
                className="bg-[#0F0F0F] border-white/10 mt-1" rows={2}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-white/60">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="pi-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {Object.keys(CAT_ICON).map((k) => (
                      <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase()+k.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/60">Required tier</Label>
                <Select value={form.tier_required} onValueChange={(v) => setForm({...form, tier_required: v})}>
                  <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="pi-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="workshop">Workshop ($147)</SelectItem>
                    <SelectItem value="course">Course ($247)</SelectItem>
                    <SelectItem value="membership">Membership</SelectItem>
                    <SelectItem value="cohort">Cohort</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/60">Order</Label>
                <Input type="number" value={form.order}
                  onChange={(e) => setForm({...form, order: e.target.value})}
                  className="bg-[#0F0F0F] border-white/10 mt-1"/>
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-white/60">Active</Label>
                  <div className="mt-2">
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/60">Visibility</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({...form, scope: v})}>
                <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="pi-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="global">Global — everyone at this tier</SelectItem>
                  <SelectItem value="user">Specific user</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.scope === "user" && (
              <div>
                <Label className="text-xs text-white/60">Assign to user</Label>
                <Select value={form.user_id} onValueChange={(v) => setForm({...form, user_id: v})}>
                  <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="pi-user">
                    <SelectValue placeholder="Pick a user…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                    {users.filter((u) => !u.is_admin).map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} · {u.email} · {u.tier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" onClick={() => setShowForm(false)}
                variant="outline"
                className="rounded-full bg-transparent border-white/10">
                Cancel
              </Button>
              <Button type="submit" data-testid="pi-submit"
                className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function blank() {
  return {
    label: "", description: "",
    category: "energy", tier_required: "free",
    order: 100, active: true,
    scope: "global", user_id: "",
  };
}
